import React, { useEffect, useState } from 'react';
import { QueryCache, QueryClient, QueryClientProvider } from 'react-query';
import { ReactQueryDevtools } from 'react-query/devtools';
import AppRoutes from './containers/routes/Routes';
import { BrowserRouter } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import TagManager from 'react-gtm-module';
import useCookie from 'react-use-cookie';
import { AuthProvider } from './context/UserAuthentication';
import { LocationProvider } from './context/StoreLocation';
import ScrollToTop from './components/ScrollToTop';
import { applyIphoneChromeDocumentClass, setRequestId, setVisitorId } from './helpers/helperMethods';
import { Provider } from 'react-redux';
import store, { persistor } from './redux/store/store';
import { PersistGate } from 'redux-persist/integration/react';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { logErrorMessage } from './helpers/toast.helper';
import Authentication from './components/Authentication/Authentication';
import Radar from 'radar-sdk-js';
import 'radar-sdk-js/dist/radar.css';
import Geocode from 'react-geocode';
import {
  GOOGLE_API_KEY,
  ROUTE_CATERING,
  CATERING_CAFE_ZUPAS_URL,
  UPDATE_EMAIL,
  BRANCH_IO_API_KEY,
  EMAIL_SUBSCRIPTION,
  TRIGGER_EVENT_SESSIONM,
  RADAR_PUBLISHABLE_KEY,
} from './constants';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { brazeInititalize } from './helpers/brazeHelper';
import BrazeBannerIdentitySync from './components/BrazeBannerIdentitySync';
import LogOutAction from './components/Authentication/LogOutAction';
import { HandleFallBackUrlsProvider } from './context/HandleFallBackUrlsContext';
import { logCustomEvent, logInAppMessageImpression, requestImmediateDataFlush, showInAppMessage, subscribeToInAppMessage } from '@braze/web-sdk';
import { PushNotificationPrompt } from './components/PushNotificationPrompt';
import InAppMessage from './components/Molecules/Modal/InAppMessage';
import branch from 'branch-sdk'
import BranchBannerController from './controllers/BranchBannerController';
import { queryClient } from './react-query-hooks/queryClient';
import LocationMenuController from './controllers/LocationMenuController';
import UserDataController from './controllers/UserDataController';
import {
  isChunkLoadErrorEvent,
  reloadOnceForChunkError,
} from './helpers/chunkLoadError';
import { registerOptimizelyBridge } from './helpers/checkout/optimizelyBridge';
import { initBaseNavHeightCssVars } from './helpers/navHeightCssVars';
import { pulse } from './analytics';
import { initGa4Analytics } from './analytics/ga4';
import PulseViewTracker from './analytics/PulseViewTracker';
import PulseCheckoutStartedTracker from './analytics/PulseCheckoutStartedTracker';
import { initCookieYesConsentTracking } from './helpers/cookieConsentTracking';
Geocode.setApiKey(GOOGLE_API_KEY);
Geocode.setLanguage('en');

brazeInititalize();
branch.init(BRANCH_IO_API_KEY,  {
  disableSessionRecover: true
}, function(err, data) {
  console.log(err, data);
});

try {
  if (RADAR_PUBLISHABLE_KEY) {
    Radar.initialize(RADAR_PUBLISHABLE_KEY);
  }
} catch (_) {}

// Auto-reload once when a stale JS/CSS chunk 404s after a new deployment.
// CSS chunk errors fire as synchronous errors (not promise rejections), so we
// need both window.onerror and unhandledrejection to catch those cases.
// React.lazy failures are handled in Routes via isChunkLoadErrorReason — they do
// not bubble as unhandledrejection because React catches the import() promise.
window.addEventListener(
  'error',
  (event) => {
    if (isChunkLoadErrorEvent(event)) {
      reloadOnceForChunkError();
    }
  },
  true
);

window.addEventListener('unhandledrejection', (event) => {
  if (isChunkLoadErrorEvent(event)) {
    reloadOnceForChunkError();
  }
});

function App() {
  const [inAppMessage, setInAppMessage] = useState<{message:any,state: 'show' | typeof UPDATE_EMAIL | typeof EMAIL_SUBSCRIPTION | typeof TRIGGER_EVENT_SESSIONM}>();
  const publishableKey = process.env.REACT_APP_PUBLISHABLE_KEY;
  const stripePromise = loadStripe(publishableKey);
  const path = new URL(window.location.href).pathname;
  const [cookies, setCookie] = useCookie('EmailModal');
  
  useEffect(() => {
    if (!Boolean(process.env.REACT_APP_ENABLE_BRAZE_TRACKING)) return;
  
    // Step 1: Clear old session timestamp
    localStorage.removeItem(`ab.storage.messagingSessionStart.${process.env.REACT_APP_BRAZE_API_KEY}`);
    console.log('Cleared Braze session timestamp');
  
    // Step 2: Flush data to sync latest campaign messages
    requestImmediateDataFlush();
  
    // Step 3: Log an event to trigger fresh message fetch
    // logCustomEvent(APP_LAUNCH);
  
    // Step 4: Short delay before subscribing
    let unsubscribe;
    const timeoutId = setTimeout(() => {
      unsubscribe = subscribeToInAppMessage((message) => {
        logInAppMessageImpression(inAppMessage?.message);
        console.log('New in-app message received:', message);
        const hasValidExtras = message?.extras && Object.keys(message?.extras).length > 0;
        if (hasValidExtras) {          
          if(message.extras?.action === UPDATE_EMAIL){
          setInAppMessage({message, state: UPDATE_EMAIL});
          }else if(message.extras?.action === EMAIL_SUBSCRIPTION){
            setInAppMessage({message, state: EMAIL_SUBSCRIPTION});
          }
          else{
            setInAppMessage({message, state: 'show'});
          }
          return false;
        } else {
          showInAppMessage(message);
        }
      });
  
      console.log('Subscribed to new in-app messages');
    }, 1000);
  
    // Cleanup function to prevent memory leaks
    return () => {
      clearTimeout(timeoutId);
      if (unsubscribe && typeof unsubscribe !== 'string') {
        unsubscribe();
      }
      console.log('Cleaned up in-app message subscription');
    };
  }, []);

  // Dismiss the message 
  const dismissMessage = () => {
    if (inAppMessage) {
      setInAppMessage(null);
    }
  };

  setVisitorId();
  setRequestId();
  // Capture utm/click ids before deep-link hooks strip the URL; flush outbox.
  pulse.init();
  initGa4Analytics();

  useEffect(() => {
    initBaseNavHeightCssVars();
    applyIphoneChromeDocumentClass();
  }, []);

  useEffect(() => {
    registerOptimizelyBridge();
  }, []);

  useEffect(() => {
    /**
     * CafeZupas GTM Tag
     */
    TagManager.initialize({ gtmId: process.env.REACT_APP_GTM });

    if (cookies !== 'true') {
      document.querySelectorAll(`#root`).forEach(function (e) {
        if (e.hasAttribute('setBy')) {
          e.setAttribute('setBy', 'APP');
        }
        if (e.hasAttribute('inert')) {
          e.setAttribute('inert', '');
        }
      });
    }

    const initCookiesConsent = () => {
      if (
        process.env.REACT_APP_ENABLE_COOKIEYES === 'true' &&
        process.env.REACT_APP_COOKIEYES_SITEID
      ) {
        const cookiesScript = document.createElement('script');
        cookiesScript.async = true;
        cookiesScript.id = 'cookieyes';
        cookiesScript.src = `https://cdn-cookieyes.com/client_data/${process.env.REACT_APP_COOKIEYES_SITEID}/script.js`;

        if (!document.getElementById('cookieyes')) {
          document.head.appendChild(cookiesScript);
        }
      }
    };

    const teardownConsentTracking = initCookieYesConsentTracking();

    window.addEventListener('load', () => {
      initCookiesConsent();
      console.log('CookieYes initialization function called');
    });
    return () => {
      window.removeEventListener('load', initCookiesConsent);
      teardownConsentTracking();
    };
  }, [cookies]);

  if (path === ROUTE_CATERING) {
    window.location.href = CATERING_CAFE_ZUPAS_URL;
    return null;
  }

  return (
    <Provider store={store}>
      <PersistGate loading={<>loading...</>} persistor={persistor}>
        {/* <QueryPersistGate> */}
        <div className="App">
          <GoogleOAuthProvider
            clientId={process.env.REACT_APP_GOOGLE_SIGNIN_ID}
          >
            <QueryClientProvider client={queryClient}>
              <BrowserRouter>
                <BranchBannerController />
                <AuthProvider>
                  <BrazeBannerIdentitySync />
                  <Elements stripe={stripePromise}>
                    <LocationProvider>
                      <UserDataController />
                      <LocationMenuController />
                      <ScrollToTop>
                        <HandleFallBackUrlsProvider>
                            {inAppMessage && (
                              <InAppMessage
                                inAppMessage={inAppMessage.message}
                                closeModal={dismissMessage}
                                inAppMessageState={inAppMessage.state}
                              />
                            )}
                            <PushNotificationPrompt
                              onClose={(granted) => {
                                console.log(
                                  'User response to push prompt:',
                                  granted
                                );
                              }}
                            />
                            <LogOutAction />
                            <PulseViewTracker />
                            <PulseCheckoutStartedTracker />
                            <AppRoutes />
                        </HandleFallBackUrlsProvider>
                      </ScrollToTop>
                    </LocationProvider>
                  </Elements>
                </AuthProvider>
              </BrowserRouter>
              <ToastContainer />
              <ReactQueryDevtools />
            </QueryClientProvider>
          </GoogleOAuthProvider>
          </div>
        {/* </QueryPersistGate> */}
      </PersistGate>
    </Provider>
  );
}

export default App;
